import { useCallback } from "react";
import { useRecoilValue } from "recoil";

import {
  controlNameMetadataResolve,
  controlsSF,
  getComputedGridFieldName,
  gridControlsSF,
  ICustomField,
  NO_CACHE_KEY,
  UNRESOLVED_GRID_FIELD_PREFIX,
} from "@ryde/shared";

import { usePageContext } from "../contexts";
import { appendSuffixToEntity } from "./use-dynamic-forms.helpers";
import { useDynamicGridTranslate } from "./use-dynamic-grid.translate";

export const useDynamicFormsUtils = () => {
  const { entity } = usePageContext();
  const { getGridLabelComputed } = useDynamicGridTranslate();
  const gridControls = useRecoilValue(gridControlsSF(entity));
  const controls = useRecoilValue(controlsSF(entity));

  const getCustomControlIdByFieldName = useCallback(
    (name: string) => controls?.find(control => control.name === name)?.id,
    [controls],
  );

  const getGridControlLabelById = useCallback(
    (id: number) => {
      const control = controls?.find(c => c.id === id);

      if (!control) {
        return `Control-${id}`;
      }

      return getGridLabelComputed(entity, control);
    },
    [controls, entity, getGridLabelComputed],
  );

  const getGridControlLabelByName = useCallback(
    (name: string) => {
      const control = controls?.find(c => c.name === name);

      if (!control) {
        return `Control-${name}`;
      }

      return getGridLabelComputed(entity, control);
    },
    [controls, entity, getGridLabelComputed],
  );

  const getCustomFieldById = useCallback(
    (id: number) => controls?.find(control => control.id === id),
    [controls],
  );

  const getGridControlByGridFieldName = useCallback(
    (gridFieldName: string): ICustomField | undefined => {
      return gridControls?.find(control => {
        return (
          (getComputedGridFieldName(control) ||
            controlNameMetadataResolve(control) ||
            control.name) === gridFieldName
        );
      });
    },
    [gridControls],
  );

  const getCustomFieldNameById = useCallback(
    (id: number) =>
      controls?.find(control => control.id === id)?.name as string,
    [controls],
  );

  const getControlDropdownSource = useCallback(
    (id: number) =>
      controls?.find(control => control.id === id)?.formAttributes
        ?.dropItemsSource,
    [controls],
  );

  const getControlDropdownSourceCacheKey = useCallback(
    (id: number) => {
      const control = controls?.find(c => c.id === id);

      if (!control?.formAttributes?.dropItemsSource) {
        return NO_CACHE_KEY;
      }

      return appendSuffixToEntity({
        entity: control?.formAttributes?.dropItemsSource,
        suffix: control?.formAttributes?.dropdownItemsSuffixOverride,
      });
    },
    [controls],
  );

  const getControlFormAttributes = useCallback(
    (id: number) =>
      controls?.find(control => control.id === id)?.formAttributes,
    [controls],
  );

  const getControlFilterDropdownCriteria = useCallback(
    (id: number) =>
      controls?.find(control => control.id === id)?.formAttributes
        ?.filterDropdownCriteria,
    [controls],
  );

  const getControlSelectItems = useCallback(
    (id: number) =>
      controls?.find(control => control.id === id)?.formAttributes?.selectItems,
    [controls],
  );

  const getFormFieldNameByControlId = useCallback(
    (id: number): string => {
      return controls.find(x => x.id === id)?.name || "";
    },
    [controls],
  );

  const getGridFieldNameByControlId = useCallback(
    (id: number): string => {
      const control = controls.find(x => x.id === id);

      if (!control) {
        return `${UNRESOLVED_GRID_FIELD_PREFIX}${id}`;
      }

      return getComputedGridFieldName(control);
    },
    [controls],
  );

  return {
    getCustomFieldById,
    getGridControlByGridFieldName,
    getCustomControlIdByFieldName,
    getGridControlLabelById,
    getGridControlLabelByName,
    getCustomFieldNameById,
    getFormFieldNameByControlId,
    getGridFieldNameByControlId,
    getControlDropdownSource,
    getControlDropdownSourceCacheKey,
    getControlFormAttributes,
    getControlSelectItems,
    getControlFilterDropdownCriteria,
  };
};
